import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Check, OpenInNewRounded } from "@mui/icons-material";
import { Grid, Zoom } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { darken, lighten, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import * as AssessmentSession from "../../state/redux/assessmentSession";
import * as PatientSession from "../../state/redux/patientSession";
import { formatIsoDateString } from "../../utils/helpers";
import AlertDialog from "../dialogs/alertDialog";
import AssessmentWizardDialog from "../dialogs/assessmentWizardDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetContent from "../ui/widgetContent";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PatientAssessments = props => {
  const { assessmentODMs, title = "Assessments", assessmentTemplates } = props;

  const [selectedAssessmentODM, setSelectedAssessmentODM] = useState(null);
  const [showAssessmentWizardDialog, setShowAssessmentWizardDialog] = useState(false);
  const [sentFromListItem, setSentFromListItem] = useState(false);

  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  const getAssessmentStatus = assessment => {
    if (assessment.omniData.completeDate != null) {
      return `Completed: ${formatIsoDateString(assessment.omniData.completeDate)}`;
    }
    if (assessment.omniData.startDate != null) {
      return `Started: ${formatIsoDateString(assessment.omniData.startDate)}`;
    }
    return "Not Started";
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (assessmentODMs != null) {
      setTitleText(assessmentODMs ? `${assessmentODMs.length} Completed` : "");
    } else setTitleText("");
  }, [assessmentODMs]);

  useEffect(() => {
    if (assessmentTemplates == null) {
      props.loadAssessmentTemplatesRequest();
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentTemplates]);

  const getWidgetContent = () => {
    if (assessmentODMs.length !== 0) {
      return (
        <WidgetList>
          {assessmentODMs.map((assessment, index) => {
            return (
              <ListItem
                dense
                key={`assessment${assessment._id}`}
                disableGutters={true}
                sx={{
                  padding: theme.spacing(1),
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  boxShadow: theme.shadows[3],
                  backgroundColor: theme.palette.foreground.main,
                  color: theme.palette.foreground.contrastText,
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.secondary.main,
                    background: lighten(theme.palette.secondary.main, 0.75),
                    border: `none`,
                  },
                }}
              >
                <Tooltip
                  title="Assessment Total Score"
                  placement="top"
                  enterDelay={500}
                  enterNextDelay={500}
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 200, easing: "ease-in-out" }}
                >
                  {Object.prototype.toString.call(assessment.omniData.assessmentResults) ===
                  "[object Object]" ? (
                    <OmniAvatar
                      sx={[
                        {
                          width: 42,
                          height: 42,
                          fontSize: "1.8rem",
                          boxShadow: theme.shadows[1],
                          cursor: "unset",
                        },
                        assessment.omniData.assessmentResults.finalResult.toString().length >=
                          3 && {
                          fontSize: "1.4rem",
                        },
                        assessment.omniData.assessmentResults.finalResult.toString().length >=
                          4 && { fontSize: "1.2rem" },
                        assessment.omniData.assessmentResults.finalResult >= 14 && {
                          color: darken(theme.palette.error.main, 0.5),
                          backgroundColor: lighten(theme.palette.error.main, 0.5),
                          border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                        },
                        assessment.omniData.assessmentResults.finalResult > 6 &&
                          assessment.omniData.assessmentResults.finalResult < 14 && {
                            color: darken(theme.palette.warning.main, 0.5),
                            backgroundColor: lighten(theme.palette.warning.main, 0.5),
                            border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                          },
                        assessment.omniData.assessmentResults.finalResult <= 6 && {
                          color: darken(theme.palette.success.main, 0.5),
                          backgroundColor: lighten(theme.palette.success.main, 0.5),
                          border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                        },
                        (assessment.omniData.omniType === constants.ar1001Prescreen ||
                          assessment.omniData.omniType ===
                            constants.assessmentIDs.omniHealthHistory) && {
                          color: darken(theme.palette.success.main, 0.5),
                          backgroundColor: lighten(theme.palette.success.main, 0.5),
                          border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                        },
                      ]}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      {assessment.omniData.omniType === constants.ar1001Prescreen ||
                      assessment.omniData.omniType === constants.assessmentIDs.omniHealthHistory ? (
                        <Check />
                      ) : (
                        assessment.omniData.assessmentResults.finalResult
                      )}
                    </OmniAvatar>
                  ) : (
                    <OmniAvatar
                      sx={[
                        {
                          width: 42,
                          height: 42,
                          fontSize: "1.8rem",
                          boxShadow: theme.shadows[1],
                          cursor: "unset",
                        },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults
                            .find(scoringMethod => scoringMethod.isDefault === true)
                            .finalResult.toString().length >= 3 && {
                            fontSize: "1.4rem",
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults
                            .find(scoringMethod => scoringMethod.isDefault === true)
                            .finalResult.toString().length >= 4 && { fontSize: "1.2rem" },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult >= 14 && {
                            color: darken(theme.palette.error.main, 0.5),
                            backgroundColor: lighten(theme.palette.error.main, 0.5),
                            border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult > 6 &&
                          (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                            assessment.omniData.omniType !==
                              constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult < 14 && {
                            color: darken(theme.palette.warning.main, 0.5),
                            backgroundColor: lighten(theme.palette.warning.main, 0.5),
                            border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult <= 6 && {
                            color: darken(theme.palette.success.main, 0.5),
                            backgroundColor: lighten(theme.palette.success.main, 0.5),
                            border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                          },
                      ]}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      {
                        assessment.omniData.assessmentResults.find(
                          scoringMethod => scoringMethod.isDefault === true,
                        ).finalResult
                      }
                    </OmniAvatar>
                  )}
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      variant="overline"
                      sx={{
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: `-${theme.spacing(1)}`,
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {getAssessmentStatus(assessment)}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      sx={{
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: `-${theme.spacing(1.5)}`,
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {assessment.omniData.title}
                    </Typography>
                  }
                  onClick={e => {
                    try {
                      e.stopPropagation();
                      setSelectedAssessmentODM(assessment);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                />
                <OmniEditButton
                  title={"Assessment Results"}
                  onClick={e => {
                    try {
                      e.stopPropagation();
                      setSentFromListItem(true);
                      console.log(sentFromListItem);
                      setSelectedAssessmentODM(assessment);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  icon={<OpenInNewRounded size="large" />}
                />
              </ListItem>
            );
          })}
        </WidgetList>
      );
    }
    return (
      <WidgetContent>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignContent="space-evenly"
          sx={{ height: "100%", color: theme.palette.commonground.contrastText }}
        >
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            sx={{ height: "56px", marginBottom: theme.spacing(1) }}
          >
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Typography variant="overline" sx={{ lineHeight: 2, height: "56px" }}>
                Looks like you haven't completed any prescreening assessments yet...
              </Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="center" sx={{ height: "56px" }}>
            <Grid item>
              <OmniResponsiveButton
                buttonText="Start Prescreening"
                tooltipPlacement="bottom"
                icon={null}
                tooltipTitle="Click here to find out if you qualify for the latest trial"
                onClick={() => {
                  setShowAssessmentWizardDialog(true);
                  // gtag("event", "click", {
                  //   event_category: "Patient_Actions",
                  //   event_label: "Start_Assessment",
                  // });
                }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="center" sx={{ height: "56px" }}>
            <Grid item>
              <Typography variant="overline" align="center">
                Click the Start button to get going!
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </WidgetContent>
    );
  };

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={assessmentODMs == null}></WidgetTitle>

      {assessmentODMs == null && <WidgetSkeleton />}
      {assessmentODMs != null && getWidgetContent()}

      {showAssessmentWizardDialog && (
        <AssessmentWizardDialog
          open={showAssessmentWizardDialog || selectedAssessmentODM}
          displayResultsAssessmentODM={selectedAssessmentODM}
          onClose={() => {
            setSelectedAssessmentODM(null);
            setShowAssessmentWizardDialog(false);
          }}
        />
      )}

      {assessmentToDelete && (
        <AlertDialog
          open={assessmentToDelete}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this assessment?"}
          onAccept={() => {
            props.deleteAssessmentRequest(assessmentToDelete._id);
            setAssessmentToDelete(null);
          }}
          onClose={() => {
            setAssessmentToDelete(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  assessmentTemplates: state.assessmentSession.assessmentTemplates,
  assessmentODMs: state.patientSession.patientAssessments,
  patientODM: state.patientSession.patientODM,
  userODM: state.userSession.userODM,
  awsGroup: state.userSession.awsGroup,
});

const mapDispatchToProps = {
  updateAssessmentRequest: PatientSession.updateAssessmentRequest,
  deleteAssessmentRequest: AssessmentSession.deleteAssessmentRequest,
  loadAssessmentTemplatesRequest: AssessmentSession.loadAssessmentTemplatesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientAssessments);
